import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Image, type ImageProps } from '@opendoor/atoms/image';
import { cn, mergeRefs } from '@opendoor/atoms/utils';
import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';

export type PropertyPhotoLoadState = 'loading' | 'loaded' | 'unavailable';

export const usePropertyPhotoLoadState = (sourceKey: string | null) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [loadedSourceKey, setLoadedSourceKey] = useState<string | null>(null);
  const [failedSourceKey, setFailedSourceKey] = useState<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    const image = imageRef.current;
    if (!sourceKey || !image?.complete) {
      return;
    }

    if (image.naturalWidth > 0) {
      setLoadedSourceKey(sourceKey);
      setFailedSourceKey(null);
    } else {
      setFailedSourceKey(sourceKey);
    }
  }, [sourceKey]);

  let state: PropertyPhotoLoadState = 'loading';
  if (sourceKey == null || failedSourceKey === sourceKey) {
    state = 'unavailable';
  } else if (loadedSourceKey === sourceKey) {
    state = 'loaded';
  }

  const onLoad = useCallback(() => {
    if (sourceKey) {
      setLoadedSourceKey(sourceKey);
      setFailedSourceKey(null);
    }
  }, [sourceKey]);

  const onError = useCallback(() => {
    if (sourceKey) {
      setFailedSourceKey(sourceKey);
    }
  }, [sourceKey]);

  return {
    imageRef,
    state,
    onLoad,
    onError,
  };
};

export const PropertyPhotoPlaceholder = ({
  state,
  className,
}: {
  state: Exclude<PropertyPhotoLoadState, 'loaded'>;
  className?: string;
}) => (
  <Box
    aria-hidden
    data-property-photo-state={state}
    className={cn(
      'items-center justify-center bg-od-bg-secondary',
      state === 'loading' && 'skeleton',
      className,
    )}
  />
);

export type PropertyPhotoImageProps = Omit<ImageProps, 'src'> & {
  src?: string | null;
  isPending?: boolean;
  containerClassName?: string;
  placeholderClassName?: string;
};

export const PropertyPhotoImage = React.forwardRef<HTMLImageElement, PropertyPhotoImageProps>(
  (
    {
      src,
      isPending = false,
      containerClassName,
      placeholderClassName,
      className,
      onLoad,
      onError,
      ...imageProps
    },
    forwardedRef,
  ) => {
    const sourceKey = src || null;
    const {
      imageRef: loadStateRef,
      state,
      onLoad: markLoaded,
      onError: markUnavailable,
    } = usePropertyPhotoLoadState(sourceKey);
    const imageRef = useMemo(
      () => mergeRefs(loadStateRef, forwardedRef),
      [forwardedRef, loadStateRef],
    );
    const placeholderState = sourceKey == null && isPending ? 'loading' : state;
    const handleLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        markLoaded();
        onLoad?.(event);
      },
      [markLoaded, onLoad],
    );
    const handleError = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        markUnavailable();
        onError?.(event);
      },
      [markUnavailable, onError],
    );

    return (
      <Box className={cn('relative overflow-hidden', containerClassName)}>
        {sourceKey && (
          <Image
            {...imageProps}
            ref={imageRef}
            src={sourceKey}
            className={className}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
        {placeholderState !== 'loaded' && (
          <PropertyPhotoPlaceholder
            state={placeholderState}
            className={cn('absolute inset-0', placeholderClassName)}
          />
        )}
      </Box>
    );
  },
);

PropertyPhotoImage.displayName = 'PropertyPhotoImage';
